import * as React from 'react';
import cn from 'classnames';
import ButtonBase from '@mui/material/ButtonBase';
import MediaQuery, { useMediaQuery } from 'react-responsive';

import SeatTooltip from './SeatTooltip/SeatTooltip';
import { useTheme } from '../../../../theme';
import { SeatPositionInRow, Seat as SeatType } from '../../../types';
import { GdsServiceById } from '@shared/lib/gdsService';
import { PassengerState } from '../../../../Checkout/store/passengers/reducers';
import { forwardRef, useContext, useState } from 'react';
import { SeatsRecommendContext } from '../../../../Checkout/components/Checkout/SeatsRecommendation/SeatsRecommendation';
import { TravellerFieldEnum, SeatComfort } from '@websky/graphql';
import { isFreeSeat as getIsFreeSeat } from '../utils';
import { getFirstCharName, getUserValue, TABLET_MIDDLE_WIDTH } from '../../../../utils';
import { useToggleable } from '../../../../hooks';
import { useConfig } from '../../../../context';
import { CdnIcon, SafeHtml } from '@shared/ui';
import { CompactMoney } from '../../../../Money';
import { useRenderers } from '../../../../renderProps';
import { isZeroPrice as getIsZeroPrice } from '@shared/lib/money';
import SeatChoose from './SeatChoose/SeatChoose';
import { useSeatPrice } from '../../../hooks';

export interface Props {
	seat: SeatType;
	onSelect: (seat: SeatType, passengerId: string) => void;
	isConfirmed?: boolean;
	isAvailable: boolean;
	occupiedBy: PassengerState;
	passenger: PassengerState;
	isSmall: boolean;
	segmentId?: string;
	passengerId: string;
	combinedSeat?: SeatType;
	isCombinedWith?: 'left' | 'right';
	onClear?: () => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	backgroundColor?: string;
	hideSeatName?: boolean;
	className?: string;
	dataColorNum?: string;
	isSeatMapWide?: boolean;
	seatPositionInRow?: SeatPositionInRow;
	isCollapsed?: boolean;
	gdsServiceById: GdsServiceById;
}

const Seat = forwardRef<HTMLButtonElement, Props>(
	(
		{
			seat,
			onSelect,
			isAvailable,
			isSmall,
			occupiedBy,
			passenger,
			isConfirmed,
			segmentId,
			passengerId,
			onClear,
			backgroundColor,
			hideSeatName,
			className,
			combinedSeat,
			isCombinedWith,
			onMouseEnter,
			onMouseLeave,
			dataColorNum,
			isSeatMapWide,
			isCollapsed,
			seatPositionInRow,
			gdsServiceById
		},
		ref
	) => {
		const theme = useTheme('SeatMap').SeatStyles;
		const { isOpen: showSeatInfo, open: openSeatInfo, close: closeSeatInfo } = useToggleable(false);
		const isMobile = useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });

		const [isTooltipOpened, setTooltipOpened] = useState(false);

		const seatRecommendContext = useContext(SeatsRecommendContext);

		const showPriceInSeat = useConfig()?.global?.seatMap?.showPriceInSeat;

		const { SeatCompactMoney: SeatCompactMoneyOverride = CompactMoney } = useRenderers();

		const seats = React.useMemo(() => {
			if (combinedSeat) {
				return [seat, combinedSeat];
			}

			return [seat];
		}, [combinedSeat, seat, isCombinedWith]);

		const isFreeSeat = getIsFreeSeat(seat, passengerId);
		const isAvailableSeat = (isAvailable || occupiedBy) && !isConfirmed;

		const handleSelect = () => {
			if (isMobile) {
				onMouseEnter();
				openSeatInfo();
			} else if (isAvailableSeat) {
				onSelect(seat, passengerId);
				setTooltipOpened(false);
			}
		};

		const onCloseSeatInfo = () => {
			closeSeatInfo();
			onMouseLeave();
		};

		const handleMobileSelect = (seat: SeatType) => {
			if (isAvailableSeat) {
				onSelect(seat, passengerId);
			}

			onCloseSeatInfo();
		};

		const preselectSeatForCurrentPassenger = seatRecommendContext.isOpen
			? seatRecommendContext.rowsMap[segmentId]?.seats[passengerId]?.seat.number
			: false;

		const isPreselect = seatRecommendContext.isOpen
			? seatRecommendContext.rowsMap[segmentId]?.seatsMap.hasOwnProperty(seat.number)
			: false;

		const seatTestStatus = !seat.isExistent
			? 'nonexistent'
			: occupiedBy
			? 'occupied'
			: isConfirmed
			? 'confirmed'
			: isPreselect
			? 'recommended'
			: !isAvailableSeat
			? 'unavailable'
			: isFreeSeat
			? 'free'
			: 'available';

		let seatLabel = `<span>${seat.row}</span><span>${seat.letter}</span>`;
		let shortPassengerName = false;

		if (occupiedBy) {
			const firstName = getUserValue(occupiedBy, TravellerFieldEnum.FirstName),
				lastName = getUserValue(occupiedBy, TravellerFieldEnum.LastName);

			if (firstName && lastName) {
				seatLabel = getFirstCharName({ lastName, firstName });

				shortPassengerName = true;
			} else {
				seatLabel = (parseInt(occupiedBy.id) + 1).toString();
			}
		}

		const { seatPrice, seatPriceDetails } = useSeatPrice(seat, passengerId, gdsServiceById);

		/**
		 * Показываем цену в "месте" если:
		 * - включено в конфиге
		 * - место не выбранно
		 * - не широкофизюляжный брт
		 * - место доступно
		 *
		 * Если у места isZeroPrice (amount === 0), то сам элемент выводим, но скрываем его стилями.
		 * Чтобы была возможность включать по запрсу а/к.
		 */
		const showSeatPrice = showPriceInSeat && seatPrice && !occupiedBy && !isSmall && isAvailableSeat;
		const showExtendedSeatIcon = seat?.isMoreSpaceForLegs && isAvailableSeat;
		const isZeroPrice = getIsZeroPrice(seatPrice);

		const button = (
			<ButtonBase
				ref={ref}
				data-rfisc={seat.rfisc}
				data-seat-number={seat.number}
				data-color-num={dataColorNum}
				data-test-id="seat"
				data-seat-status={seatTestStatus}
				data-passenger-id={occupiedBy?.id}
				className={cn(theme.seat, className, {
					[theme.seat_small]: isSmall,
					[theme.seat_free]: isFreeSeat,
					[theme.seat_standart]: seat.service && seat.service.comfort === SeatComfort.Standart, // Property=EconomyBase
					[theme.seat_business]: seat.service && seat.service.comfort === SeatComfort.Comfort, // Property=Premium
					[theme.seat_preferred]: seat.service && seat.service.comfort === SeatComfort.Preferred, // Property1=Front
					[theme.seat_unexistent]: !seat.isExistent,
					[theme.seat_unrecline]: !seat.isRecline,
					[theme.seat_unavailable]: !isAvailableSeat,
					[theme.seat_extended]: seat?.isMoreSpaceForLegs,
					[theme.seat_occupied]: occupiedBy,
					[theme.seat_confirmed]: isConfirmed,
					[theme.seat_recommendCurrent]: isPreselect && preselectSeatForCurrentPassenger === seat.number,
					[theme.seat_recommend]: isPreselect,
					[theme.seat_customColor]: !!backgroundColor,
					[theme.seat_combinedLeft]: isCombinedWith === 'left',
					[theme.seat_combinedRight]: isCombinedWith === 'right',
					[theme.seat_withPrice]: showSeatPrice,
					[theme.seat_withZeroPrice]: isZeroPrice,
					[theme.seat_aisle]: seat.isAisle,
					[theme.seat_collapsed]: isCollapsed
					// [theme.male]: seat.isSeat && seat.occupiedBy === Gender.Male,
					// [theme.female]: props.seat.isSeat && props.seat.occupiedBy === Gender.Female
				})}
				disabled={!isAvailableSeat}
				onClick={handleSelect}
				onMouseEnter={isMobile ? null : onMouseEnter}
				onMouseLeave={isMobile ? null : onMouseLeave}
				style={backgroundColor ? { background: backgroundColor } : null}
			>
				<>
					{showExtendedSeatIcon && <CdnIcon className={theme.extendedIcon} icon="Seat map/Extra leg room" />}

					{seat.isExistent && (isAvailable || occupiedBy) && (
						<SafeHtml
							tag="div"
							className={cn(theme.number, { [theme.number_name]: shortPassengerName })}
							html={seatLabel}
						/>
					)}

					{seat.isNearWindowWithoutWindow && !isSeatMapWide && seatPositionInRow && (
						<span
							className={cn(theme.withoutWindow, {
								[theme.withoutWindow_left]: seatPositionInRow === 'first',
								[theme.withoutWindow_right]: seatPositionInRow === 'last'
							})}
						/>
					)}

					{showSeatPrice && <SeatCompactMoneyOverride moneyClassName={theme.seatPrice} money={seatPrice} />}

					<CdnIcon className={theme.deselectIcon} icon="Action/Close-Clear" />
				</>
			</ButtonBase>
		);

		return (
			<>
				<MediaQuery minWidth={TABLET_MIDDLE_WIDTH + 1}>
					<SeatTooltip
						isFreeSeat={isFreeSeat}
						seats={seats}
						service={seat.services?.[0]}
						isRecommend={isPreselect}
						occupiedBy={occupiedBy}
						isAvailable={isAvailable}
						totalSeatPrice={seatPrice}
						priceDetails={seatPriceDetails}
						hideSeatName={hideSeatName}
						isTooltipOpened={isTooltipOpened}
						onToggle={setTooltipOpened}
					>
						{button}
					</SeatTooltip>
				</MediaQuery>
				<MediaQuery maxWidth={TABLET_MIDDLE_WIDTH}>
					{button}

					<SeatChoose
						isOpen={showSeatInfo}
						seats={seats}
						totalSeatPrice={seatPrice}
						priceDetails={seatPriceDetails}
						isSelected={!!occupiedBy}
						isFreeSeat={isFreeSeat}
						dataColorNum={dataColorNum}
						backgroundColor={backgroundColor}
						onClose={onCloseSeatInfo}
						onSelect={handleMobileSelect}
					/>
				</MediaQuery>
			</>
		);
	}
);

export default React.memo(Seat);
